import React, { useEffect, useMemo, useRef } from 'react';
import styles from './index.module.less';
import TopBlank from 'components/TopBlank';
import { routeUrlNoSider } from 'router/routeUrl';
import { Image } from 'antd';
import classnames from 'classnames';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isPathHidden } from 'utils/language';
import { useLanguageNavigate } from 'hooks/useLanguageRoute';

import gsap from 'gsap';
import { useGSAP } from '@gsap/react';

interface IProps {
  onMenuItemClick?: (path: string) => void;
  showMenuLayer?: boolean;
}

const MenuLayer = React.memo(({ showMenuLayer, onMenuItemClick }: IProps) => {
  const location = useLocation();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const navigate = useLanguageNavigate();

  const isFirstRender = useRef(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useGSAP(() => {
    const tl = gsap.timeline({
      defaults: {
        duration: 0.25,
        ease: 'power2.inOut',
      },
    });
    if (isFirstRender.current) {
      tl.set(`.${styles.menuLayer}`, {
        yPercent: '-100',
      });
      tl.set(`.${styles.menuItem}`, {
        opacity: 0,
        // xPercent: 100,
        translateY: '-10px',
      });
      isFirstRender.current = false;
    } else {
      if (showMenuLayer) {
        tl.to(`.${styles.menuLayer}`, {
          yPercent: 0,
        });
        tl.to(`.${styles.menuItem}`, {
          opacity: 1,
          // xPercent: 0,
          translateY: '0px',
          stagger: 0.1,
        });
      } else {
        tl.to(`.${styles.menuItem}`, {
          opacity: 0,
          // xPercent: 100,
          translateY: '-10px',
          stagger: 0.1,
        });
        tl.to(
          `.${styles.menuLayer}`,
          {
            yPercent: '-100',
          },
          '-=0.2',
        );
      }
    }
  }, [showMenuLayer]);

  const handleMenuItemClick = (path: string) => {
    onMenuItemClick?.(path);
    if (path === location.pathname) {
      return;
    }
    navigate(path);
  };

  const menuList = useMemo(() => {
    const currentLanguage = (i18n.language || 'en').split('-')[0];
    return routeUrlNoSider.filter((el) => {
      // 必须有 menuIcon 才显示
      if (!el.menuIcon) return false;
      // 根据语言配置动态隐藏导航
      if (el.path && isPathHidden(el.path, currentLanguage)) {
        return false;
      }
      return true;
    });
  }, [routeUrlNoSider, i18n.language]);

  return (
    <div className={styles.menuLayer}>
      <TopBlank />
      <div className={styles.menuLayerContent}>
        {menuList.map((el) => {
          const isActive = location.pathname.startsWith(el.path);
          return (
            <div
              key={el.path}
              className={classnames(styles.menuItem, {
                [styles.active]: isActive,
                flex: true,
              })}
            >
              <Image src={el.menuIcon} alt={t(el.menuName)} preview={false} width=".7rem" />
              <h2
                onClick={() => handleMenuItemClick(el.path)}
                className={classnames({ [styles.active]: isActive, [styles[el.color!]]: true })}
              >
                {t(el.menuName)}
              </h2>
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default MenuLayer;
